import { IPathfindingGoalCondition } from "./IPathfindingGoalCondition";
import { IntRectangle } from "./IntRectangle";
import { PathfindingGraph } from "./PathfindingGraph";

/**
 * A goal condition for the Pathfinder that is satisfied on any node adjacent to a given rectangle.
 * @author npuschke
 *
 */
export class PathfindingGoalConditionTargetRectAdjacent implements IPathfindingGoalCondition {
    private _target: IntRectangle;
    private _useCorners: boolean;

    /**
     * Creates a GoalCondition that is satisfied at nodes adjacent to the given rectangle.
     * @param target The rectangle that valid target nodes must be adjacent to.
     * @param useCorners Whether the goal condition should also be satisfied at the nodes diagonally adjacent to the given rectangle.
     */
    constructor(target: IntRectangle, useCorners: boolean = false) {
        this._target = target.clone();
        this._useCorners = useCorners;
    }

    /**
     * Determines whether this goal condition is satisfied at the given coordinates.
     * @param x The horizontal coordinate of the potential target node.
     * @param y The vertical coordinate of the potential target node.
     * @return <code>true</code> if the node is the target; <code>false</code> otherwise.
     *
     */
    public isSatisfied(x: number, y: number): boolean {
        if (this._useCorners) {
            return (((x == this._target.left - 1 || x == this._target.right) && y >= this._target.top - 1 && y <= this._target.bottom) || ((y == this._target.top - 1 || y == this._target.bottom) && x >= this._target.left && x < this._target.right));
        }
        else {
            return (((x == this._target.left - 1 || x == this._target.right) && y >= this._target.top && y < this._target.bottom) || ((y == this._target.top - 1 || y == this._target.bottom) && x >= this._target.left && x < this._target.right));
        }
    }

    /**
     * Roughly checks if the goal condition is satisfied for any node in the graph at all. If not, the path search can be aborted early.
     * @param graph The graph that will be searched.
     * @return <code>true</code> if no node satisfies the goal condition; <code>false</code> otherwise.
     *
     */
    public isUnreachable(graph: PathfindingGraph): boolean {
        let x: number = this._target.left - 1;
        let y: number = 0;

        // check left border
        for (y = this._target.top; y < this._target.bottom; y++)
            if (graph.hasNodeAtPosition(x, y)) {
                return false;
            }

        // check right border
        x = this._target.right;

        for (y = this._target.top; y < this._target.bottom; y++)
            if (graph.hasNodeAtPosition(x, y)) {
                return false;
            }

        // check top border
        y = this._target.top - 1;

        for (x = this._target.left; x < this._target.right; x++)
            if (graph.hasNodeAtPosition(x, y)) {
                return false;
            }

        // check bottom border
        y = this._target.bottom;

        for (x = this._target.left; x < this._target.right; x++)
            if (graph.hasNodeAtPosition(x, y)) {
                return false;
            }

        // check corners
        if (this._useCorners) {
            if (graph.hasNodeAtPosition(this._target.left - 1, this._target.top - 1) || graph.hasNodeAtPosition(this._target.right, this._target.top - 1) || graph.hasNodeAtPosition(this._target.left - 1, this._target.bottom) || graph.hasNodeAtPosition(this._target.right, this._target.bottom)) {
                return false;
            }
        }

        return true;
    }
}
